/**
 * Sticky-tab resolution for the Workflow Overview page (spec 2026-07-11,
 * Task 15 URL-param deep-link). Pulled out of `WorkflowOverviewPage.tsx`
 * so the page module only exports the component — `initialTabFor` is a
 * plain function, and co-locating it with the component broke React Fast
 * Refresh (`react-refresh/only-export-components`).
 */

export const TAB_STORAGE_PREFIX = 'wf-tab:'

export function readStoredTab(workflowId: string | undefined): string {
  if (!workflowId || typeof window === 'undefined') return 'overview'
  return window.localStorage.getItem(`${TAB_STORAGE_PREFIX}${workflowId}`) ?? 'overview'
}

// Static allowlist for validating an incoming `?tab=` URL param — NOT the
// source of truth for which tabs actually render (that's the dynamically
// built `availableTabs` in WorkflowOverviewPage, which varies by workflow
// kind and edit state). This list only decides whether a param value is
// well-formed enough to seed initial state; the `tabValue` fallback in the
// component still guards against a known-but-unavailable tab (e.g.
// `settings` in read mode).
const KNOWN_TABS = [
  'overview', 'agenda', 'automations', 'messages', 'handoff', 'signals', 'settings', 'versions', 'readiness',
  // SA-448 (D11): the process kind's Steps tab — the record panel's map
  // links `?tab=steps&step=<id>` here. Since SA-459 the `step` opens the
  // step drawer over the page as well (WorkflowOverviewPage's `selectedStep`).
  'steps',
] as const

/**
 * URL param wins over the stored tab so review findings can deep-link.
 * An unknown param falls through to the stored value rather than
 * rendering a tab that doesn't exist.
 *
 * SA-454 (spec §6): a `step` in the URL opens the Steps tab on its own — the
 * record panel's map always sends `?tab=steps&step=`, but a shared or
 * hand-built link need not. An explicit known tab still wins. On a workflow
 * with no Steps tab the component's `availableTabs` fallback sends it back to
 * overview, which is why no definition is needed here.
 */
export function initialTabFor(workflowId: string | undefined, paramTab: string | null, paramStep?: string | null): string {
  if (paramTab && (KNOWN_TABS as readonly string[]).includes(paramTab)) return paramTab
  if (paramStep) return 'steps'
  return readStoredTab(workflowId)
}
